/**
 * useRosterMode — 老師模式 / 救生員模式 on the teacher mobile pages, for accounts
 * that hold both 老師 and 救生員 (permissions.ts canSwitchRosterMode; PO 2026-09-16).
 *
 * In 救生員模式 the pages behave exactly like a 救生員's (useRoleContext resolves
 * the page role to lifeguard and sends viewAsRole 'lifeguard'); the backend
 * honours that only for holders of the role. A UI preference, remembered per
 * account on this device (localStorage — a missing / blocked store just means
 * 老師模式). It never changes what the account may do elsewhere.
 */
import { computed, ref } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { canSwitchRosterMode } from '@/config/permissions';

export type RosterMode = 'teacher' | 'lifeguard';

const STORAGE_PREFIX = 'oovi.rosterMode.';
/** In-memory copy per uid (reactive), filled lazily from localStorage. */
const modes = ref<Record<string, RosterMode>>({});

function readStored(uid: string): RosterMode {
  try {
    return localStorage.getItem(STORAGE_PREFIX + uid) === 'lifeguard' ? 'lifeguard' : 'teacher';
  } catch {
    return 'teacher';
  }
}

export function useRosterMode() {
  const authStore = useAuthStore();

  const canSwitch = computed(() => canSwitchRosterMode(authStore.userRoles as string[]));

  /** Current mode — 'teacher' whenever the account cannot switch. */
  const rosterMode = computed<RosterMode>(() => {
    const uid = authStore.user?.uid;
    if (!uid || !canSwitch.value) return 'teacher';
    return modes.value[uid] ?? readStored(uid);
  });

  const setRosterMode = (mode: RosterMode) => {
    const uid = authStore.user?.uid;
    if (!uid || !canSwitch.value) return;
    modes.value = { ...modes.value, [uid]: mode };
    try {
      localStorage.setItem(STORAGE_PREFIX + uid, mode);
    } catch {
      // Private mode / blocked storage: the switch still works for this session.
    }
  };

  return { canSwitch, rosterMode, setRosterMode };
}
